import { useMemo } from 'react';

import styled from 'styled-components';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from 'components/DataTable';
import { EllipsisTooltip } from 'components/EllipsisTooltip';

import { dateFromString, formatDateForDisplay } from 'helpers/dateTimes';
import { renderAsPrice } from 'helpers/price';

import { getColorsFromState, SUPPLY_ORDER_STATES_LABELS } from './constants/states';
import type { SupplyOrderRow } from './constants/supplyOrderViews';

function formatDate(date?: string | null): string {
    return date ? formatDateForDisplay(dateFromString(date), { displayDay: true }) : '-';
}

type SupplyOrdersDataTableViewProps = {
    supplyOrders: SupplyOrderRow[];
    selectedSupplyOrderIds: string[];
    setSelectedSupplyOrderIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const SupplyOrdersDataTableView = ({
    supplyOrders,
    selectedSupplyOrderIds,
    setSelectedSupplyOrderIds,
}: SupplyOrdersDataTableViewProps) => {
    const columns = useMemo<ColumnDef<SupplyOrderRow, unknown>[]>(
        () => [
            {
                id: 'supplier',
                accessorFn: (row) => row.supplier.name,
                header: 'Fournisseur',
                cell: ({ row }) => (
                    <EllipsisTooltip
                        text={row.original.supplier.name}
                        className="max-w-[220px] font-semibold text-foreground"
                    />
                ),
            },
            {
                accessorKey: 'number',
                header: 'N°',
                cell: ({ row }) => `#${row.original.number ?? '?'}`,
            },
            {
                accessorKey: 'state',
                header: 'État',
                cell: ({ row }) => {
                    const { state, weeklyMenusStartDate, isMerged } = row.original;
                    const { backgroundColor, labelColor } = getColorsFromState(state, weeklyMenusStartDate, isMerged);
                    return (
                        <span
                            className="inline-block whitespace-nowrap rounded-sm px-2 py-0.5 text-xs font-semibold"
                            style={{ backgroundColor, color: labelColor }}
                        >
                            {SUPPLY_ORDER_STATES_LABELS[state]}
                        </span>
                    );
                },
                sortingFn: (rowA, rowB) =>
                    (SUPPLY_ORDER_STATES_LABELS[rowA.original.state] || '').localeCompare(
                        SUPPLY_ORDER_STATES_LABELS[rowB.original.state] || '',
                    ),
            },
            {
                id: 'warehouseColumn',
                accessorFn: (row) => row.warehouseColumn?.name ?? '-',
                header: 'Entrepôt',
            },
            {
                id: 'priceHT',
                accessorFn: (row) => row.priceHT ?? 0,
                header: 'Prix HT',
                cell: ({ row }) => (row.original.priceHT != null ? renderAsPrice(row.original.priceHT) : 'Non calculé'),
            },
            {
                accessorKey: 'orderDate',
                header: 'Commandée',
                cell: ({ row }) => formatDate(row.original.orderDate),
            },
            {
                accessorKey: 'dateDeliveryScheduled',
                header: 'Prévue',
                cell: ({ row }) => (
                    <span className={row.original.scheduledDeliveryDateValidity ? undefined : 'text-destructive'}>
                        {formatDate(row.original.dateDeliveryScheduled)}
                    </span>
                ),
            },
            {
                accessorKey: 'dateDelivery',
                header: 'Livrée',
                cell: ({ row }) => formatDate(row.original.dateDelivery),
            },
            {
                accessorKey: 'weeklyMenusStartDate',
                header: 'Menus',
                cell: ({ row }) => formatDate(row.original.weeklyMenusStartDate),
            },
            {
                id: 'issues',
                accessorFn: (row) => row.issues.length,
                header: 'Litiges',
                cell: ({ getValue }) => {
                    const count = getValue<number>();
                    return count > 0 ? <span className="font-semibold text-destructive">{count}</span> : '0';
                },
            },
        ],
        [],
    );

    return (
        <TableContainer>
            <DataTable<SupplyOrderRow>
                columns={columns}
                data={supplyOrders}
                filterMode="client"
                getRowId={(row) => row._id}
                getRowLink={(row) => `/supply-order/${row._id}`}
                defaultSortBy="dateDeliveryScheduled"
                defaultSortOrder="desc"
                searchPlaceholder="Rechercher dans le tableau…"
                emptyMessage="Aucune fiche d'achat."
                enableSelection
                selectedKeys={selectedSupplyOrderIds}
                onSelectionChange={setSelectedSupplyOrderIds}
                enableVirtualization
                disableUrlSync
            />
        </TableContainer>
    );
};

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;
